import { proctoringThunk } from '../store/exam/thunks';
import { setNotification } from '../store/general/slice';
import { useDispatch } from '../store/store';
import { useEffect, useRef } from 'react';

/**
 * Reports the applicant leaving and returning to the exam tab, and warns them the first
 * time it happens. Nothing is reported while the exam is paused — a break between two
 * questions is theirs to spend as they like.
 */
export const useProctoring = (
    userId: string | null,
    examId: string | undefined,
    questionNumber: number,
    type: 'multipleChoiceExam' | 'codingExam',
    isPaused?: boolean,
) => {
    const dispatch = useDispatch();
    const isBlurred = useRef(false);
    // The listeners are bound once per question, so the pause flag is read through a ref
    // rather than captured — otherwise a break starting mid-question goes unnoticed.
    const isPausedRef = useRef(isPaused);
    isPausedRef.current = isPaused;

    useEffect(() => {
        const report = (actionId: number) => {
            dispatch(proctoringThunk({
                type,
                userId,
                examId,
                actionId,
                question: questionNumber,
            }));
        };

        const handleFocus = () => {
            if (!isBlurred.current || isPausedRef.current) return;

            isBlurred.current = false;
            report(2);
            dispatch(setNotification(['Warning, you must stay in the exam tab until the end.', 'error']));
        };

        const handleBlur = () => {
            if (isBlurred.current || isPausedRef.current) return;

            isBlurred.current = true;
            report(1);
        };

        const handleVisibilityChange = () => {
            document.visibilityState === 'hidden' ? handleBlur() : handleFocus();
        };

        window.addEventListener('focus', handleFocus);
        window.addEventListener('blur', handleBlur);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('focus', handleFocus);
            window.removeEventListener('blur', handleBlur);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [questionNumber]);
};
